import { useEffect } from 'react';

const BASE_URL = 'https://www.klasowisko.pl';

// Mapowanie segmentow URL na czytelne etykiety dla BreadcrumbList
const BREADCRUMB_LABELS = {
    'search': 'Wyszukiwarka',
    'friends': 'Znajomi',
    'messages': 'Wiadomosci',
    'groups': 'Grupy',
    'profile': 'Profil',
    'board': 'Tablica',
    'gallery': 'Galeria',
    'events': 'Wydarzenia',
    'blog': 'Blog',
    'artykul': 'Artykul',
    'zorki': 'Zorki',
    'polecenia': 'Polecenia',
    'viewers': 'Goscie',
    'marriage': 'Pary klasowe',
    'dedications': 'Dedykacje',
    'timecapsule': 'Kapsula czasu',
    'voting': 'Glosowania',
    'ranking': 'Ranking',
    'quiz': 'Quizy',
    'games': 'Gry',
    'achievements': 'Osiagniecia',
    'admin': 'Administracja',
    'school': 'Szkola',
    'szkola': 'Szkola',
    'privacy': 'Polityka prywatnosci',
    'terms': 'Regulamin',
    'about': 'O nas',
    'contact': 'Kontakt',
    'help': 'Pomoc',
    'advertising': 'Reklama',
};

function buildBreadcrumbLd(pathname, dynamicLabel) {
    if (!pathname || pathname === '/' || pathname === '') return null;
    const segments = pathname.split('/').filter(Boolean);
    if (segments.length === 0) return null;

    const items = [
        { "@type": "ListItem", "position": 1, "name": "Strona glowna", "item": BASE_URL }
    ];

    let cumPath = '';
    segments.forEach((seg, i) => {
        cumPath += '/' + seg;
        const isLast = i === segments.length - 1;
        // Slug-like segment (np. /blog/jak-zaczac) - uzyj dynamicLabel jesli ostatni i podany,
        // inaczej Capitalize segmentu.
        let name;
        if (BREADCRUMB_LABELS[seg]) {
            name = BREADCRUMB_LABELS[seg];
        } else if (isLast && dynamicLabel) {
            name = dynamicLabel;
        } else {
            name = seg.charAt(0).toUpperCase() + seg.slice(1).replace(/-/g, ' ').slice(0, 60);
        }
        items.push({
            "@type": "ListItem",
            "position": i + 2,
            "name": name,
            "item": BASE_URL + cumPath,
        });
    });

    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": items,
    };
}

/**
 * Hook to set per-page SEO meta tags dynamically.
 * Updates title, description, canonical, OG tags, and JSON-LD.
 */
export function usePageSEO({ title, description, url, image, type = 'website', jsonLd = null, noindex = false }) {
    useEffect(() => {
        // Title
        if (title) {
            document.title = title;
        }

        // Meta description
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc && description) {
            metaDesc.setAttribute('content', description.slice(0, 160));
        }

        // Robots (noindex dla prywatnych stron: profil, znajomi, wiadomosci, admin, 404)
        let robotsTag = document.querySelector('meta[name="robots"]');
        if (!robotsTag) {
            robotsTag = document.createElement('meta');
            robotsTag.setAttribute('name', 'robots');
            document.head.appendChild(robotsTag);
        }
        robotsTag.setAttribute('content', noindex ? 'noindex, nofollow' : 'index, follow');

        // Canonical
        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) {
            canonical = document.createElement('link');
            canonical.setAttribute('rel', 'canonical');
            document.head.appendChild(canonical);
        }
        canonical.setAttribute('href', url || `${BASE_URL}${window.location.pathname}`);

        // OG Tags
        const ogTags = {
            'og:title': title,
            'og:description': description?.slice(0, 160),
            'og:url': url || `${BASE_URL}${window.location.pathname}`,
            'og:image': image || `${BASE_URL}/icons/icon-512x512.png`,
            'og:type': type,
            'og:locale': 'pl_PL',
            'og:site_name': 'Klasowisko.pl',
        };

        Object.entries(ogTags).forEach(([property, content]) => {
            if (!content) return;
            let meta = document.querySelector(`meta[property="${property}"]`);
            if (!meta) {
                meta = document.createElement('meta');
                meta.setAttribute('property', property);
                document.head.appendChild(meta);
            }
            meta.setAttribute('content', content);
        });

        // Twitter Card
        const twitterTags = {
            'twitter:card': 'summary_large_image',
            'twitter:title': title,
            'twitter:description': description?.slice(0, 160),
            'twitter:image': image || `${BASE_URL}/icons/icon-512x512.png`,
        };

        Object.entries(twitterTags).forEach(([name, content]) => {
            if (!content) return;
            let meta = document.querySelector(`meta[name="${name}"]`);
            if (!meta) {
                meta = document.createElement('meta');
                meta.setAttribute('name', name);
                document.head.appendChild(meta);
            }
            meta.setAttribute('content', content);
        });

        // JSON-LD Structured Data (supports single object or array)
        const jsonLdItems = Array.isArray(jsonLd) ? [...jsonLd] : (jsonLd ? [jsonLd] : []);

        // Auto-BreadcrumbList: dodajemy jesli strona nie podala wlasnego
        const hasBreadcrumb = jsonLdItems.some(item => item && item['@type'] === 'BreadcrumbList');
        if (!hasBreadcrumb && typeof window !== 'undefined') {
            const auto = buildBreadcrumbLd(window.location.pathname, title?.split(' - ')[0]);
            if (auto) jsonLdItems.push(auto);
        }

        // Remove all previous JSON-LD
        document.querySelectorAll('script[data-seo="page-jsonld"]').forEach(el => el.remove());

        jsonLdItems.forEach((item, i) => {
            if (!item) return;
            const script = document.createElement('script');
            script.setAttribute('type', 'application/ld+json');
            script.setAttribute('data-seo', 'page-jsonld');
            script.textContent = JSON.stringify(item);
            document.head.appendChild(script);
        });

        // Cleanup on unmount
        return () => {
            document.querySelectorAll('script[data-seo="page-jsonld"]').forEach(el => el.remove());
            // Robots wracamy do domyslnego po opuszczeniu strony noindex
            if (noindex) {
                const r = document.querySelector('meta[name="robots"]');
                if (r) r.setAttribute('content', 'index, follow');
            }
        };
    }, [title, description, url, image, type, jsonLd, noindex]);
}
